import { useMemo, useState } from 'react';
import { useApp } from '../store';
import { liveLabels } from '../types';
import { nextLabelColour } from '../area-colours';
import RenameField from './RenameField';
import ColourField from './ColourField';

/*
 * THE BOARD'S LABELS — a managed object, #462. Modelled on AreaSettings: a flat
 * row-per-label list that opens ONE editor at a time, so the list you came to read
 * is not buried under the controls for changing it.
 *
 * A label is a cross-cutting tag — "needs vendor", "security review" — independent
 * of what part of the product a card touches (its area) or which push of work it
 * belongs to (its epic). A card still carries label NAMES; this screen is where a
 * board names them, gives them an OPTIONAL colour, and retires the ones it no
 * longer uses.
 *
 * TWO THINGS IT DOES NOT HAVE, both on purpose. No ORDER controls — areas render
 * in a chosen sequence, labels are a flat set shown alphabetically. No LEAD — a
 * label is a tag, not a part of the estate somebody owns. A new label is AUTO-
 * COLOURED on creation (Vinnie, 5 Nov) from the shared identity palette — here and
 * inline on a card — so a fresh label is tellable at a glance; the colour stays
 * clearable for the rare label a board wants to keep a plain neutral chip.
 */
export default function LabelSettings() {
  const labels = useApp((s) => s.labels);
  const problem = useApp((s) => s.labelsProblem);
  const issues = useApp((s) => s.issues);
  const createLabel = useApp((s) => s.createLabel);
  const editLabel = useApp((s) => s.editLabel);
  const retireLabel = useApp((s) => s.retireLabel);
  const restoreLabel = useApp((s) => s.restoreLabel);

  const [open, setOpen] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState('');
  const [colour, setColour] = useState('');
  const [says, setSays] = useState('');

  const live = liveLabels(labels);
  const retired = labels.filter((l) => l.retiredAt).sort((a, b) => a.name.localeCompare(b.name));

  /* How many cards carry each label — counted by NAME, since that is what a card
     stores. Retired labels still count the cards that already wear them. */
  const used = useMemo(() => {
    const n: Record<string, number> = {};
    for (const i of issues) for (const l of i.labels) {
      const k = l.trim().toLowerCase();
      n[k] = (n[k] ?? 0) + 1;
    }
    return n;
  }, [issues]);

  const run = async (p: Promise<string>) => setSays(await p);
  const cards = (nm: string) => {
    const c = used[nm.trim().toLowerCase()] ?? 0;
    return c ? `${c} ${c === 1 ? 'card' : 'cards'}` : 'none yet';
  };

  return (
    <section className="panel">
      <h2>Labels</h2>
      <p className="hint">
        A cross-cutting tag you put on any card — &ldquo;security review&rdquo;, &ldquo;quick win&rdquo;,
        &ldquo;needs vendor&rdquo; — independent of its area or epic. A new label is given a colour
        automatically, shown as the dot beside it on a card; change or clear that colour here.
      </p>

      {/* A refused read is not an empty list, and must not be drawn as one — #289. */}
      {problem && <p className="form-error">The labels could not be read — {problem}</p>}

      {live.length === 0 && !problem && (
        <p className="hint">
          None yet. A label typed on a card still works; add one here to give it a colour and
          keep track of it.
        </p>
      )}

      <ul className="area-list">
        {live.map((l) => (
          <li key={l.id} className={`area-row ${open === l.id ? 'is-open' : ''}`}>
            <div className="area-line">
              {l.colour ? (
                <span className="area-dot" style={{ background: l.colour }} />
              ) : (
                <span className="area-dot epics-sw-none" />
              )}
              <span className="area-name-flat">{l.name}</span>
              <span className="area-used">{cards(l.name)}</span>
              <button
                className="btn btn-small"
                aria-expanded={open === l.id}
                onClick={() => setOpen(open === l.id ? null : l.id)}
              >
                {open === l.id ? 'Done' : 'Change'}
              </button>
              <button className="btn btn-small" onClick={() => void run(retireLabel(l.id))}>
                Remove
              </button>
            </div>

            {open === l.id && (
              <div className="area-editor">
                <RenameField value={l.name} onRename={(name) => void run(editLabel(l.id, { name }))} />
                <div className="area-field">
                  Colour
                  <ColourField
                    value={l.colour ?? ''}
                    onChange={(c) => void run(editLabel(l.id, { colour: c }))}
                    allowNone
                    label={l.name}
                  />
                </div>
              </div>
            )}
          </li>
        ))}
      </ul>

      {adding ? (
        <div className="area-editor is-adding">
          <label className="area-field">
            Name
            <input
              className="input"
              value={name}
              autoFocus
              placeholder="security review, quick win…"
              onChange={(e) => setName(e.target.value)}
            />
          </label>
          <div className="area-field">
            Colour
            <ColourField value={colour} onChange={setColour} allowNone label="the new label" />
          </div>
          <div className="area-adds">
            <button
              className="btn btn-small btn-primary"
              disabled={!name.trim()}
              onClick={async () => {
                const res = await createLabel({ name, colour: colour || null });
                setSays('error' in res ? res.error : '');
                if ('id' in res) {
                  setName('');
                  setColour('');
                  setAdding(false);
                }
              }}
            >
              Add it
            </button>
            <button className="btn btn-small" onClick={() => { setAdding(false); setName(''); setColour(''); setSays(''); }}>
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <button
          className="btn btn-small"
          onClick={() => { setColour(nextLabelColour(labels)); setAdding(true); }}
        >
          Add a label
        </button>
      )}

      {says && <p className="form-error">{says}</p>}

      <p className="hint area-note">
        <b>Removing a label does not clear it off the cards using it.</b> It stops being offered
        on new work and stays where it already is, so last month&rsquo;s figures still add up.
        A removed label moves to a Retired list below &mdash; Put back restores it.
      </p>

      {retired.length > 0 && (
        <>
          <h3 className="area-heading">Retired</h3>
          <ul className="area-list">
            {retired.map((l) => (
              <li key={l.id} className="area-row is-retired">
                <div className="area-line">
                  {l.colour ? (
                    <span className="area-dot" style={{ background: l.colour }} />
                  ) : (
                    <span className="area-dot epics-sw-none" />
                  )}
                  <span className="area-name-flat">{l.name}</span>
                  <span className="area-used">{cards(l.name)}</span>
                  <button className="btn btn-small" onClick={() => void run(restoreLabel(l.id))}>
                    Put back
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </>
      )}
    </section>
  );
}
